import { useSplitView } from '@/app/_common/split-view/split-view-context'
import { useTurnParsed } from '@/app/chat/_common/use-turn-parsed'
import { Turn } from '@/hooks/api/use-turn-status-query'
import React from 'react'
import { PlanStepsProvider } from './context'
import { PlanSteps } from './plan-steps'
import { PlanStepUpdatesList } from './step-updates-list'

type PlanStepsContainerProps = Readonly<{
  status?: Turn['status']
  error?: Turn['error']
  turnId: string
  enableAnimation: boolean
  workflowId: string
}>

export const PlanStepsContainer: React.FC<PlanStepsContainerProps> = ({
  status,
  error,
  turnId,
  enableAnimation,
  workflowId,
}) => {
  const { parsedSteps } = useTurnParsed({ turnId, workflowId })
  const [expanded, setExpanded] = React.useState(true)
  const [expandedStepIds, setExpandedStepIds] = React.useState<Set<string>>(new Set())

  // Track which steps we've already auto-expanded to avoid re-expanding
  const autoExpandedRef = React.useRef<string | null>(null)
  const autoExpandedStepIdsRef = React.useRef<Set<string>>(new Set())

  React.useEffect(() => {
    const isLoading = status === 'IN_PROGRESS' || status === 'PLANNING' || status === 'QUEUED'
    const currentSteps = parsedSteps ?? []

    // Case 1: Auto-expand all steps once if the turn status is loading
    if (isLoading && autoExpandedRef.current !== turnId) {
      const allStepIds = currentSteps
        .filter((step: any) => step.outputDisplayData.tasks.length > 0)
        .map((step: any) => step.stepId)
      setExpandedStepIds(new Set(allStepIds))
      autoExpandedRef.current = turnId
      autoExpandedStepIdsRef.current = new Set(allStepIds)
      return
    }

    // Case 2: Auto-expand individual steps that are initially added AND in progress, then collapse them when completed
    if (isLoading) {
      const stepsWithTasks = currentSteps.filter(
        (step: any) => step.outputDisplayData.tasks.length > 0,
      )

      const newInProgressSteps = stepsWithTasks.filter(
        (step: any) =>
          step.status === 'IN_PROGRESS' && !autoExpandedStepIdsRef.current.has(step.stepId),
      )

      const completedSteps = stepsWithTasks.filter(
        (step: any) => step.status === 'SUCCESS' && autoExpandedStepIdsRef.current.has(step.stepId),
      )

      if (newInProgressSteps.length > 0 || completedSteps.length > 0) {
        setExpandedStepIds((prev) => {
          const next = new Set(prev)

          newInProgressSteps.forEach((step: any) => {
            next.add(step.stepId)
            autoExpandedStepIdsRef.current.add(step.stepId)
          })

          completedSteps.forEach((step: any) => {
            next.delete(step.stepId)
            autoExpandedStepIdsRef.current.delete(step.stepId)
          })

          return next
        })
      }
    }
  }, [status, turnId, parsedSteps])

  const { stepsDisplayData, lastAgent } = React.useMemo(() => {
    const parsed = parsedSteps ?? []
    const lastAgent = parsed.findLast(({ agentData }: any) => agentData)?.agentData ?? null
    return { stepsDisplayData: parsed, lastAgent }
  }, [parsedSteps])

  const { addTab } = useSplitView()

  const handleToggleExpand = () => setExpanded((prev) => !prev)

  const handleToggleStepExpand = (stepId: string, expand: boolean | null = null) => {
    setExpandedStepIds((prev) => {
      const next = new Set(prev)
      const isCurrentlyExpanded = next.has(stepId)
      if (expand === null) {
        // Toggle
        if (isCurrentlyExpanded) {
          next.delete(stepId)
        } else {
          next.add(stepId)
        }
      } else {
        // Set to specific value
        if (expand) {
          next.add(stepId)
        } else {
          next.delete(stepId)
        }
      }
      return next
    })
  }

  const contextValue = React.useMemo(
    () => ({
      turnId,
      enableAnimation,
      expandedStepIds,
      setExpandedStepIds,
      addTab,
    }),
    [turnId, enableAnimation, expandedStepIds, setExpandedStepIds, addTab],
  )

  return (
    <PlanStepsProvider value={contextValue}>
      <PlanSteps
        steps={stepsDisplayData}
        status={status}
        error={error}
        lastAgent={lastAgent}
        expanded={expanded}
        onToggleExpand={handleToggleExpand}
        expandedStepIds={expandedStepIds}
        onToggleStepExpand={handleToggleStepExpand}
        setExpandedStepIds={setExpandedStepIds}
        turnId={turnId}
        workflowId={workflowId}
        renderStepUpdatesList={(step) => (
          <PlanStepUpdatesList
            tasks={step.outputDisplayData.tasks}
            isInProgress={step.status === 'IN_PROGRESS'}
            enableAnimation={enableAnimation}
            stepId={step.stepId}
            key={step.stepId}
            workflowId={workflowId}
          />
        )}
      />
    </PlanStepsProvider>
  )
}
